import { useEffect, useState } from 'react'
import { adminService } from '../../identity/services/admin'
import { authService } from '../../identity/services/auth'
import { CheckCircle, AlertCircle } from 'lucide-react'

const ROLE_OPTIONS = [
  { value: 'TELECALLER',    label: 'Telecaller',     locked: true  },
  { value: 'TEAM_LEADER',   label: 'Team Leader',    locked: false },
  { value: 'INSURANCE_HEAD', label: 'Insurance Head', locked: false },
]

export default function LeadAssignmentPage() {
  const tenantId = authService.getCurrentUser()?.tenant_id
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['TELECALLER'])
  const [loading, setLoading]             = useState(true)
  const [saving, setSaving]               = useState(false)
  const [msg, setMsg]                     = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (!tenantId) return
    adminService.getLeadAssignmentSettings(tenantId)
      .then(s => setSelectedRoles(s.manual_assignable_roles))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId])

  const toggle = (role: string) => {
    if (role === 'TELECALLER') return // always locked
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    )
    setMsg(null)
  }

  const save = async () => {
    if (!tenantId) return
    setSaving(true)
    setMsg(null)
    try {
      const result = await adminService.updateLeadAssignmentSettings(tenantId, selectedRoles)
      setSelectedRoles(result.manual_assignable_roles)
      setMsg({ type: 'success', text: 'Lead assignment settings saved.' })
    } catch {
      setMsg({ type: 'error', text: 'Failed to save settings.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Lead Assignment</h1>
        <p className="page-subtitle">Choose which roles can be assigned leads manually.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><span className="spinner" /></div>
      ) : (
        <div className="card p-6 space-y-4" style={{ maxWidth: 480 }}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            The selected roles will appear in the assignment dropdown when reassigning a lead.
            Telecallers are always included.
          </p>

          <div className="space-y-3">
            {ROLE_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className="flex items-center gap-3 cursor-pointer"
                style={{ opacity: opt.locked ? 0.6 : 1 }}
              >
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(opt.value)}
                  disabled={opt.locked}
                  onChange={() => toggle(opt.value)}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--brand-primary)' }}
                />
                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {opt.label}
                  {opt.locked && (
                    <span className="ml-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      (always included)
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>

          {msg && (
            <div className={msg.type === 'success' ? 'alert-success' : 'alert-danger'}>
              {msg.type === 'success'
                ? <CheckCircle className="h-4 w-4 shrink-0" />
                : <AlertCircle className="h-4 w-4 shrink-0" />}
              <span>{msg.text}</span>
            </div>
          )}

          <div className="flex justify-end">
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
